import { useEffect, useState } from 'react';
import { Card, RadioCardGroup } from '@playerbase/ui';
import type { PlaytestParticipationRule } from '@playerbase/types';

import { useShell } from '../../../../shell/use-shell.js';
import { fetchDiscordConnectionForGame } from '../../../../games/discord-data.js';
import { useDiscordGuildChannels } from '../../../../games/use-discord-guild-channels.js';
import { useDiscordGuildRoles } from '../../../../games/use-discord-guild-roles.js';
import { DiscordChannelMultiSelect } from '../../../game-settings/discord/discord-channel-multi-select.js';
import { DiscordRoleMultiSelect } from '../../../game-settings/discord/discord-role-multi-select.js';

interface ParticipationRuleCardProps {
  rule: PlaytestParticipationRule;
  onChange: (rule: PlaytestParticipationRule) => void;
}

type Kind = PlaytestParticipationRule['kind'];

const KIND_OPTIONS: ReadonlyArray<{ value: Kind; label: string; description: string }> = [
  {
    value: 'open',
    label: 'Open',
    description: 'Anyone paired to this game can receive this flow’s DMs.',
  },
  {
    value: 'channel_members',
    label: 'Channel members',
    description: 'Only paired testers who are members of these channels receive DMs.',
  },
  {
    value: 'role_members',
    label: 'Role members',
    description: 'Only paired testers holding any of these roles receive DMs.',
  },
];

export function ParticipationRuleCard({
  rule,
  onChange,
}: ParticipationRuleCardProps): JSX.Element {
  const { activeGame } = useShell();
  const [connectionId, setConnectionId] = useState<string | null>(null);
  const { channels, loading: channelsLoading } = useDiscordGuildChannels(connectionId);
  const { roles, loading: rolesLoading } = useDiscordGuildRoles(connectionId);

  useEffect(() => {
    if (activeGame === null) {
      setConnectionId(null);
      return;
    }
    let cancelled = false;
    void fetchDiscordConnectionForGame(activeGame.id).then((conn) => {
      if (cancelled) return;
      setConnectionId(conn?.id ?? null);
    });
    return () => {
      cancelled = true;
    };
  }, [activeGame]);

  function handleKindChange(value: string): void {
    const kind = value as Kind;
    if (kind === 'open') onChange({ kind: 'open' });
    else if (kind === 'channel_members')
      onChange({ kind: 'channel_members', channelIds: [] });
    else onChange({ kind: 'role_members', roleIds: [] });
  }

  const hasConnection = connectionId !== null;
  const channelCount = rule.kind === 'channel_members' ? rule.channelIds.length : 0;
  const roleCount = rule.kind === 'role_members' ? rule.roleIds.length : 0;

  return (
    <Card>
      <Card.Header>
        <Card.Title>Who gets feedback requests for this playtest flow?</Card.Title>
        <Card.Description>
          Filters which paired testers receive this flow&rsquo;s DMs when
          an event fires. Only registered testers get feedback requests -
          pairing is configured under Recruitment settings.
        </Card.Description>
      </Card.Header>
      <Card.Content className="flex flex-col gap-4">
        <RadioCardGroup
          name="participation-rule-kind"
          value={rule.kind}
          onChange={handleKindChange}
          options={KIND_OPTIONS}
        />

        {rule.kind === 'channel_members' ? (
          <section className="flex flex-col gap-2">
            <header className="flex items-baseline justify-between gap-3">
              <h3 className="text-sm font-semibold text-text">Channels</h3>
              <p className="text-xs text-text-muted">
                {channelCount === 0 ? 'None selected' : `${channelCount} selected`}
              </p>
            </header>
            <DiscordChannelMultiSelect
              channels={channels}
              selectedIds={rule.channelIds}
              onChange={(next) => onChange({ kind: 'channel_members', channelIds: next })}
              loading={channelsLoading}
              emptyMessage={
                hasConnection
                  ? 'The bot has no readable channels in this server yet.'
                  : 'Connect Discord to pick channels.'
              }
              disabled={!hasConnection}
            />
          </section>
        ) : null}

        {rule.kind === 'role_members' ? (
          <section className="flex flex-col gap-2">
            <header className="flex items-baseline justify-between gap-3">
              <h3 className="text-sm font-semibold text-text">Roles</h3>
              <p className="text-xs text-text-muted">
                {roleCount === 0 ? 'None selected' : `${roleCount} selected`}
              </p>
            </header>
            <DiscordRoleMultiSelect
              roles={roles}
              selectedIds={rule.roleIds}
              onChange={(next) => onChange({ kind: 'role_members', roleIds: next })}
              loading={rolesLoading}
            />
          </section>
        ) : null}
      </Card.Content>
    </Card>
  );
}
